import { useState, useEffect, useRef, RefObject, KeyboardEventHandler } from 'react';
import { noop } from 'utils/noop';
import { useSelector } from 'react-redux';
import { AVATAR_SIZES } from 'ui/avatar';
import { DiamondLogo } from 'ui/logo';
import {
  ESCAPE_KEY,
  SPACE_KEY,
  UP_ARROW,
  DOWN_ARROW,
  RIGHT_ARROW,
  LEFT_ARROW,
  END,
  HOME,
} from 'constants/keyCodes';
import {
  selectDoesUserHaveAccessCheck,
  selectDoesUserHaveRbacPermission,
  selectFirstName,
  selectIsMissionControlHomepageFeatureFlagOn,
  selectLastName,
  selectUserAvatarUrl,
  selectUserProject,
} from 'core/userContext';
import { NavLogo } from 'modules/nav/components/NavLogo';
import {
  QuickNavWrapper,
  PrimaryMenuContainer,
  PrimaryMenu,
  PrimaryMenuHeader,
  PrimaryMenuHeaderTitle,
  StyledDivider,
  ChevronRight,
  SecondaryMenu,
  ProjectSelectionTitle,
  NavigationItemsContainer,
  StyledAvatar,
} from './QuickNav.styles';
import { PRIMARY_MENU, NAVIGATION_ITEMS } from '../constants';
import { PrimaryOption } from './PrimaryOption';
import { SecondaryOption } from './SecondaryOption';
import { ProjectSelection } from './ProjectSelection';
import { LinkSubsection } from './LinkSubsection';
import { getTabbableChildren } from './utils';
import { ProjectPermission } from 'modules/domains/project/permissions';
import { refContainsFocus } from './utils';
import { Nullable } from 'utils/tsHelpers';
import { NavigationItemKey } from '../types';

const content = {
  header: 'Quick access',
  supportText: 'Support',
};
export type QuickNavProps = {
  /** Directly Controls whether the Quick Nav is visible */
  isVisible?: boolean;
  /** Function to change the isVisible boolean */
  toggleNav: () => void;
  /** Element to send focus too on close */
  triggerEl?: RefObject<HTMLElement>;
};

const PROJECT_SELECTOR = 'project-selector';

export const QuickNav = ({
  isVisible = false,
  toggleNav = noop,
  triggerEl,
  ...props
}: QuickNavProps) => {
  // used for indicating a click from the keyboard has occured
  const [keyFlag, setKeyFlag] = useState(false);
  // stores the currently selected menu option [title, ref]
  const [[selectedTitle, selectedRef], setSelected] = useState<
    [Nullable<string>, Nullable<HTMLElement>]
  >([null, null]);
  // stores title of the currently hovered menu option
  const [hovered, setHovered] = useState<Nullable<string>>(null);
  // value used in multiple places to see if second menu is open and hold the second menu value
  const isSecondMenuOpen = selectedTitle || hovered;

  // these refs are used to send focus to their children and check if focus is within
  const quickNavEl = useRef<Nullable<HTMLDivElement>>(null);
  const primaryMenuEl = useRef<Nullable<HTMLDivElement>>(null);
  const secondaryMenuEl = useRef<Nullable<HTMLDivElement>>(null);

  const isMissionControlHomepageFeatureFlagOn = useSelector(
    selectIsMissionControlHomepageFeatureFlagOn,
  );

  const doesUserHaveAccess = useSelector(selectDoesUserHaveAccessCheck);
  const doesUserHaveRBAC = useSelector(selectDoesUserHaveRbacPermission);

  const checkRBAC = (rbacPermission?: ProjectPermission) => {
    return !rbacPermission || doesUserHaveRBAC(rbacPermission);
  };

  const firstName = useSelector(selectFirstName);
  const lastName = useSelector(selectLastName);
  const userAvatarUrl = useSelector(selectUserAvatarUrl);

  const project = useSelector(selectUserProject);

  // if isVisible changes and quick nav is visible (basically on open) focus first tabbable child
  // if nav is toggled opposite then focus trigger
  useEffect(() => {
    if (isVisible && !refContainsFocus(quickNavEl)) {
      getTabbableChildren(primaryMenuEl.current)[0]?.el.focus();
    } else {
      triggerEl?.current?.focus();
    }
  }, [isVisible]);

  // this waits until the secondary menu is open to send focus to the secondary menus first child
  // key flag is to indicate that a click has occured from the keyboard
  useEffect(() => {
    if (keyFlag) {
      getTabbableChildren(secondaryMenuEl.current)[0].el.focus();
      setKeyFlag(false);
    }
  }, [keyFlag]);

  // return early if not visible
  if (!isVisible) {
    return null;
  }

  const handlePrimaryOptionClick = (title: string, currentTarget: HTMLElement) => {
    if (title !== selectedTitle) {
      // select if option is not selected already
      setSelected([title, currentTarget]);
    } else {
      // deselect if we click on a option that is already selected
      setSelected([null, null]);
    }
  };

  const clearHover = () => setHovered(null);

  const handleBlur = () => {
    // checks to see if nav contains focus if not close nav
    // on timeout to accomadate focus
    setTimeout(() => {
      // this if avoids the double toggle
      // (eg when you click out of menu by clicking the toggle triggering onblur close and toggling menu
      // without the if it would close then reopen)
      if (!refContainsFocus(quickNavEl) && triggerEl?.current !== document.activeElement) {
        toggleNav();
      }
    }, 10);
  };

  const handleKeyPress: KeyboardEventHandler<HTMLDivElement> = ({ keyCode, currentTarget }) => {
    try {
      const menuChildren = getTabbableChildren(currentTarget);
      const focusedIndex = menuChildren.findIndex((opt) => opt.isFocused);
      const isItemFocused = focusedIndex > -1;
      const isFirstItemFocused = focusedIndex === 0;
      const focusedItem = isItemFocused ? menuChildren[focusedIndex] : null;
      const isLastItemFocused = focusedIndex === menuChildren.length - 1;
      const [firstItem, lastItem] = [menuChildren[0], menuChildren[menuChildren.length - 1]];
      switch (keyCode) {
        case END: {
          lastItem.el.focus();
          break;
        }

        case HOME: {
          firstItem.el.focus();
          break;
        }

        case ESCAPE_KEY: {
          toggleNav();
          break;
        }

        case UP_ARROW: {
          if (isItemFocused && !isFirstItemFocused) {
            menuChildren[focusedIndex - 1].el.focus();
          } else {
            lastItem.el.focus();
          }
          break;
        }

        case DOWN_ARROW: {
          if (isItemFocused && !isLastItemFocused) {
            menuChildren[focusedIndex + 1].el.focus();
          } else {
            firstItem.el.focus();
          }
          break;
        }

        case RIGHT_ARROW: {
          // if focused option is support link, right arrow does nothing
          const isSupportLink = focusedItem?.el.getAttribute('data-support-link');
          if (!isSupportLink && primaryMenuEl.current === currentTarget) {
            // If the focused menu option is not selected, click it and set key flag to true
            if (focusedItem?.el.textContent !== selectedTitle) {
              focusedItem?.el.click();
              setKeyFlag(true);
            } else {
              // If the focused menu option is already selected, then the secondary menu is already open, so focus on secondary menu's first child
              (secondaryMenuEl.current?.children?.[0] as HTMLElement)?.focus();
            }
          }
          break;
        }

        // returns to primary menu from secondary menu, focuses on the ref of the primary menu option, then deselects
        case LEFT_ARROW: {
          if (secondaryMenuEl.current === currentTarget) {
            selectedRef?.focus();
            setSelected([null, null]);
          }
          break;
        }

        // enter and space handled normally by html buttons
        // but space doesnt work for <a> elements
        case SPACE_KEY: {
          focusedItem?.el.click();
          setKeyFlag(true); // for menu buttons to redirect focus to secondary menu
          break;
        }

        default: {
          return;
        }
      }
    } catch (err) {
      console.error(err); // eslint-disable-line
    }
  };

  return (
    isVisible && (
      <QuickNavWrapper
        data-test="quick-nav"
        onMouseLeave={clearHover}
        onBlur={handleBlur}
        ref={quickNavEl}
        {...props}
      >
        <PrimaryMenuContainer>
          <PrimaryMenu
            data-test="quick-nav-primarymenu"
            onKeyDown={handleKeyPress}
            ref={primaryMenuEl}
          >
            <PrimaryMenuHeader>
              {/* Primary Menu Header Section - Logo + Header Text */}
              {isMissionControlHomepageFeatureFlagOn ? (
                <NavLogo height={24} onMouseEnter={clearHover} data-test="quick-nav-logo" />
              ) : (
                <DiamondLogo height={24} onMouseEnter={clearHover} data-test="quick-nav-logo" />
              )}
              <PrimaryMenuHeaderTitle onMouseEnter={clearHover} data-test="quick-nav-title">
                {content.header}
              </PrimaryMenuHeaderTitle>
            </PrimaryMenuHeader>

            <StyledDivider onMouseEnter={clearHover} data-test="quick-nav-divider-1" />

            {/* Primary Menu Menu Section - Menu options, which trigger the secondary menu */}
            {PRIMARY_MENU.map(
              (title, idx) =>
                checkRBAC(
                  /**
                   * WARNING: it is possible `rbacPermission` is an array (ProjectPermission[]),
                   * but we are currently not handling this case. Leaving it as is for this PR to avoid
                   * breaking changes, but we should update `checkRBAC` to handle this case in the future.
                   */
                  NAVIGATION_ITEMS[title.toUpperCase() as NavigationItemKey]
                    .rbacPermission as ProjectPermission,
                ) && (
                  <PrimaryOption
                    key={title}
                    title={title}
                    onClick={({ currentTarget }) => handlePrimaryOptionClick(title, currentTarget)}
                    onMouseEnter={() => setHovered(title)}
                    hover={title === selectedTitle}
                    data-test={`quick-nav-menuoption-${idx}`}
                  >
                    <ChevronRight height={24} width={24} />
                  </PrimaryOption>
                ),
            )}

            <StyledDivider onMouseEnter={clearHover} data-test="quick-nav-divider-2" />
            <PrimaryOption
              key={project?.name}
              title={
                <ProjectSelectionTitle>
                  <StyledAvatar
                    imgSrc={userAvatarUrl}
                    size={AVATAR_SIZES.MEDIUM}
                    firstName={firstName}
                    lastName={lastName}
                  />
                  <div className="title-text">{project?.name}</div>
                </ProjectSelectionTitle>
              }
              onClick={({ currentTarget }) =>
                handlePrimaryOptionClick(PROJECT_SELECTOR, currentTarget)
              }
              onMouseEnter={() => setHovered(PROJECT_SELECTOR)}
              hover={PROJECT_SELECTOR === selectedTitle}
              data-test={`quick-nav-menuoption-${PROJECT_SELECTOR}`}
            >
              <ChevronRight height={24} width={24} />
            </PrimaryOption>
          </PrimaryMenu>
        </PrimaryMenuContainer>

        {/* If something is selected or hovered open Secondary Menu */}
        {isSecondMenuOpen && (
          <SecondaryMenu>
            {
              /* Shows the Secondary Menu for the Selected item by default, if not the hovered item */
              isSecondMenuOpen === PROJECT_SELECTOR ? (
                <ProjectSelection
                  data-test="quick-nav-projectselectionmenu"
                  ref={secondaryMenuEl}
                  onKeyDown={handleKeyPress}
                />
              ) : (
                <NavigationItemsContainer
                  data-test="quick-nav-secondarymenu"
                  ref={secondaryMenuEl}
                  onKeyDown={handleKeyPress}
                >
                  {NAVIGATION_ITEMS[
                    isSecondMenuOpen.toUpperCase() as NavigationItemKey
                  ].dropdownOptions.map(
                    (
                      {
                        title,
                        linksSubsection,
                        permission,
                        featureFlags: requiredFeatureFlags,
                        rbacPermission,
                        hideFromProspect,
                        ...moreProps
                      },
                      idx,
                    ) =>
                      /* Creates a linkSubsection if linkSubsection is true, otherwise renders a Secondary Link Option(Router Link) */
                      linksSubsection ? (
                        <LinkSubsection
                          key={title}
                          title={title}
                          data-test={`quick-nav-linksubsection`}
                          {...moreProps}
                        />
                      ) : (
                        doesUserHaveAccess(
                          permission,
                          requiredFeatureFlags ?? [],
                          rbacPermission as number,
                          hideFromProspect,
                        ) && (
                          <SecondaryOption
                            data-test={`quick-nav-secondarylink-${idx}`}
                            key={title}
                            title={title}
                            shouldOpenInNewTab={
                              NAVIGATION_ITEMS[isSecondMenuOpen.toUpperCase() as NavigationItemKey]
                                .shouldOpenInNewTab
                            }
                            {...moreProps}
                          />
                        )
                      ),
                  )}
                </NavigationItemsContainer>
              )
            }
          </SecondaryMenu>
        )}
      </QuickNavWrapper>
    )
  );
};
